'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { WizardActionDialog } from '../../wizard-action-dialog/WizardActionDialog';
import {
  DOCUMENTATION_MENU_ITEM_VALUE,
  type ApplicationDocumentationInitialAction,
} from './applicationDocumentation.constants';
import type {
  ApplicationDocumentationController,
  DocumentationFileCategory,
  DocumentationPersistHandler,
} from './applicationDocumentation.types';
import type { ApplicationDocumentationViewModel } from './applicationDocumentationView.types';
import { ApplicationDocumentationFilesDialog } from './ApplicationDocumentationFilesDialog';
import { ApplicationDocumentationFocusedSection } from './ApplicationDocumentationFocusedSection';
import { ApplicationDocumentationLinksDialog } from './ApplicationDocumentationLinksDialog';
import { ApplicationDocumentationSections } from './ApplicationDocumentationSections';
import { useDocumentationDelete } from './useDocumentationDelete';
import { useDocumentationFileDialog } from './useDocumentationFileDialog';
import { useDocumentationLinksDialog } from './useDocumentationLinksDialog';
import { useInitialDocumentationAction } from './useInitialDocumentationAction';
import { usePersistDocumentationUrls } from './usePersistDocumentationUrls';
import { useStagedDocumentation } from './useStagedDocumentation';

export type ApplicationDocumentationCardProps = {
  applicationId: string | null;
  controller: ApplicationDocumentationController;
  isReadOnly?: boolean;
  hideHeader?: boolean;
  hideContent?: boolean;
  hideDocumentationAsyncFeedback?: boolean;
  onPersistReady?: (handler: DocumentationPersistHandler | null) => void;
  initialSection?: DocumentationFileCategory;
  focusedSection?: DocumentationFileCategory;
  initialDocumentationAction?: ApplicationDocumentationInitialAction;
  onInitialDocumentationActionAdvance?: () => void;
  onInitialDocumentationActionCancel?: () => void;
  showApiRequiredBadge?: boolean;
  onVisibleApiDocumentationCountChange?: (count: number) => void;
};

export const ApplicationDocumentationCard = ({
  applicationId,
  controller,
  isReadOnly,
  hideHeader,
  hideContent,
  hideDocumentationAsyncFeedback,
  onPersistReady,
  initialSection,
  focusedSection,
  initialDocumentationAction,
  onInitialDocumentationActionAdvance,
  onInitialDocumentationActionCancel,
  showApiRequiredBadge,
  onVisibleApiDocumentationCountChange,
}: ApplicationDocumentationCardProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const tCommon = useTranslations('designSystem.common');
  const isDeferred = Boolean(hideDocumentationAsyncFeedback);
  const showsSubmittingState = initialDocumentationAction !== undefined;
  const apiSectionRef = useRef<HTMLDivElement | null>(null);
  const testingReportsSectionRef = useRef<HTMLDivElement | null>(null);
  const hasHandledInitialActionRef = useRef(false);
  const initialAction = useInitialDocumentationAction({
    initialDocumentationAction,
    onAdvance: onInitialDocumentationActionAdvance,
    onCancel: onInitialDocumentationActionCancel,
  });
  const persistDocumentationUrls = usePersistDocumentationUrls({
    applicationId,
    saveDocumentationUrls: controller.saveDocumentationUrls,
  });
  const staged = useStagedDocumentation({
    applicationId,
    controller,
    isDeferred,
    persistDocumentationUrls,
  });
  const fileDialog = useDocumentationFileDialog({
    applicationId,
    isDeferred,
    showsSubmittingState,
    uploadFiles: controller.uploadFiles,
    stageFiles: staged.stageFiles,
    initialAction,
  });
  const linksDialog = useDocumentationLinksDialog({
    visibleDocumentationUrls: staged.visibleDocumentationUrls,
    isDeferred,
    showsSubmittingState,
    persistDocumentationUrls,
    stageDocumentationUrls: staged.stageDocumentationUrls,
    initialAction,
  });
  const documentationDelete = useDocumentationDelete({
    applicationId,
    isDeferred,
    controller,
    visibleDocumentationUrls: staged.visibleDocumentationUrls,
    persistDocumentationUrls,
    staged,
  });
  const requestDownloadFile = useCallback(
    ({
      category,
      fileIndex,
    }: {
      category: DocumentationFileCategory;
      fileIndex: number;
    }) => {
      if (!applicationId) {
        return;
      }

      const file = staged.visibleFilesByCategory[category][fileIndex];

      if (!file?.id) {
        return;
      }

      void controller.downloadFile({
        applicationId,
        fileId: file.id,
        fileName: file.name,
      });
    },
    [applicationId, controller, staged.visibleFilesByCategory],
  );
  const visibleApiDocumentationCount = staged.visibleFilesByCategory.api.length;
  const { persistDeferredDocumentation } = staged;
  const { open: openFileDialog } = fileDialog;
  const { open: openLinksDialog } = linksDialog;

  useLayoutEffect(() => {
    onVisibleApiDocumentationCountChange?.(visibleApiDocumentationCount);
  }, [onVisibleApiDocumentationCountChange, visibleApiDocumentationCount]);

  useEffect(() => {
    onPersistReady?.(persistDeferredDocumentation);

    return () => {
      onPersistReady?.(null);
    };
  }, [onPersistReady, persistDeferredDocumentation]);

  useEffect(() => {
    if (focusedSection || !initialSection || initialSection === 'application') {
      return;
    }

    const sectionRef =
      initialSection === 'api' ? apiSectionRef : testingReportsSectionRef;

    sectionRef.current?.scrollIntoView({ block: 'start' });
  }, [focusedSection, initialSection]);

  useEffect(() => {
    if (
      !applicationId ||
      !initialDocumentationAction ||
      hasHandledInitialActionRef.current
    ) {
      return;
    }

    hasHandledInitialActionRef.current = true;
    if (initialDocumentationAction === DOCUMENTATION_MENU_ITEM_VALUE.addFiles) {
      openFileDialog(initialSection ?? 'application');

      return;
    }

    openLinksDialog();
  }, [
    applicationId,
    initialDocumentationAction,
    initialSection,
    openFileDialog,
    openLinksDialog,
  ]);

  const viewModel: ApplicationDocumentationViewModel = {
    visibleFilesByCategory: staged.visibleFilesByCategory,
    visibleDocumentationUrls: staged.visibleDocumentationUrls,
    isReadOnly,
    isUploading: fileDialog.uploadingCategory !== null,
    busySection:
      fileDialog.uploadingCategory ?? documentationDelete.deleteSpinnerSection,
    omitFileRowId: documentationDelete.omitFileRowId,
    omitUrlRowIndex: documentationDelete.omitUrlRowIndex,
    onOpenFileDialog: (category) => {
      if (applicationId) {
        openFileDialog(category);
      }
    },
    onOpenLinksDialog: () => {
      if (applicationId) {
        openLinksDialog();
      }
    },
    onRemoveFile: documentationDelete.requestRemoveFile,
    onDownloadFile: requestDownloadFile,
    onRemoveUrl: documentationDelete.requestRemoveUrl,
  };
  const deleteItemLabel =
    documentationDelete.pendingDelete?.type === 'link'
      ? t('linkLabel').toLowerCase()
      : t('fileLabel').toLowerCase();
  const cardContent = focusedSection ? (
    <ApplicationDocumentationFocusedSection
      section={focusedSection}
      viewModel={viewModel}
    />
  ) : (
    <ApplicationDocumentationSections
      viewModel={viewModel}
      hideHeader={hideHeader}
      showApiRequiredBadge={showApiRequiredBadge}
      apiSectionRef={apiSectionRef}
      testingReportsSectionRef={testingReportsSectionRef}
    />
  );

  return (
    <>
      {hideContent ? null : cardContent}

      <ApplicationDocumentationFilesDialog
        category={fileDialog.category}
        files={fileDialog.files}
        fileUploadKey={fileDialog.fileUploadKey}
        isConfirming={fileDialog.isConfirming}
        onDismiss={fileDialog.dismiss}
        onConfirm={fileDialog.confirm}
        onFilesAccept={fileDialog.acceptFiles}
        onRemoveFile={fileDialog.removeFile}
      />

      <ApplicationDocumentationLinksDialog
        open={linksDialog.isOpen}
        urls={linksDialog.urls}
        canAddLink={linksDialog.canAddLink}
        canConfirm={linksDialog.canConfirm}
        isSaving={linksDialog.isSaving}
        onDismiss={linksDialog.dismiss}
        onUrlChange={linksDialog.changeUrl}
        onAddUrl={linksDialog.addUrl}
        onRemoveUrl={linksDialog.removeUrl}
        onSave={linksDialog.save}
      />

      {isDeferred ? null : (
        <WizardActionDialog
          open={documentationDelete.pendingDelete !== null}
          onOpenChange={documentationDelete.handleDialogOpenChange}
          title={t('deleteItemConfirmationTitle', { item: deleteItemLabel })}
          description={t('deleteConfirmationDescription')}
          action={tCommon('delete')}
          onActionClick={documentationDelete.confirmDelete}
          loading={documentationDelete.isDeletingItem}
          actionColor="fg.vulnerability.vulnerable"
        />
      )}
    </>
  );
};
